import React, { useEffect, useState } from 'react';
import { array, bool, number, object } from 'prop-types';

import { connect } from 'react-redux';

import { find, get, isEmpty } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';

import AutoComplete from '../../../components/FormFields/Autocomplete';

import * as CoreActions from '../../../actions/coreActions';
import * as WizardActions from '../../../actions/wizardActions';

import classes from './WizardStep.module.css';

const ValidationSchema = Yup.object().shape({
    equipmentType: Yup.string().required(),
    manufacturer:  Yup.string(),
    modelNumber:   Yup.string().max(45),
    serialNumber:  Yup.string().max(45)
});

const EquipmentStep = ({
    companyInfo,
    equipmentTypes,
    manufacturers,
    setProgressBarLoading,
    setValidateForm,
    targetEquipmentConfig,
    validateForm,
    ...props
}) => {
    const [targetEquipmentTypes, setTargetEquipmentTypes] = useState([]);
    const [targetManufacturers, setTargetManufacturers] = useState([]);

    useEffect(() => {
        if (validateForm) {
            formik.handleSubmit();

            setValidateForm(false);
        }
    }, [validateForm]);

    useEffect(() => {
        convertEquipmentTypes();
    }, [equipmentTypes]);

    useEffect(() => {
        convertManufacturers();
    }, [manufacturers]);

    const formik = useFormik({
        initialValues: {
            equipmentType: get(targetEquipmentConfig, 'equipmentType', {}),
            manufacturer:  get(targetEquipmentConfig, 'manufacturer', {}),
            modelNumber:   get(targetEquipmentConfig, 'modelNumber', ''),
            serialNumber:  get(targetEquipmentConfig, 'serialNumber', '')
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                activeStep,
                setActiveStep,
                setEquipmentTypeName
            } = props;

            const equipmentType = find(equipmentTypes, type => type.id === values.equipmentType.id);

            setEquipmentTypeName(equipmentType.name);
            setActiveStep(activeStep + 1);
        }
    });

    const convertEquipmentTypes = () => {
        const convertedEquipmentTypes = [
            {
                items:     [...equipmentTypes],
                name:      'Equipment Types',
                itemProps: {
                    type: 'equipmentTypes'
                }
            }
        ];

        setTargetEquipmentTypes(convertedEquipmentTypes);
    };

    const convertManufacturers = () => {
        const convertedManufacturers = [
            {
                items:     [...manufacturers],
                name:      'Manufacturers',
                itemProps: {
                    type: 'manufacturers'
                }
            }
        ];

        setTargetManufacturers(convertedManufacturers);
    };

    const handleChange = event => {
        const { name, value } = event.target;
        const { setTargetEquipmentConfig } = props;

        if (isEmpty(value)) {
            formik.handleChange(event);

            return;
        }

        const updateEquipment = {...targetEquipmentConfig};

        if (value) {
            updateEquipment[name] = value;
            setTargetEquipmentConfig(updateEquipment);
        }

        formik.handleChange(event);
    };

    return (
        <div className={classes.wizardForm}>
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/equipmentTreeIcon.svg" alt="Equipment Icon" />
                        <span>Configure Equipment</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Select the equipment type.
                        </p>
                        <p>
                            Select a manufacturer. (Optional)
                        </p>
                        <p>
                            Enter a model number and a serial number. (Optional)
                        </p>
                        <p>
                            Note: selections will be applied to each Equipment created at the end of this wizard.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.autoCompleteWrapper}>
                            <AutoComplete
                                grouped
                                label="Equipment Type"
                                name="equipmentType"
                                onBlur={formik.handleBlur}
                                onChange={handleChange}
                                options={targetEquipmentTypes}
                                placeholder="Equipment Type"
                                value={formik.values.equipmentType}
                                error={Boolean(formik.errors.equipmentType) && Boolean(formik.touched.equipmentType)}
                                helperText={Boolean(formik.touched.equipmentType) && formik.errors.equipmentType}
                            />
                        </FormControl>
                        <FormControl className={classes.autoCompleteWrapper}>
                            <AutoComplete
                                grouped
                                label="Manufacturer"
                                name="manufacturer"
                                onBlur={formik.handleBlur}
                                onChange={handleChange}
                                options={targetManufacturers}
                                placeholder="Manufacturer"
                                value={formik.values.manufacturer}
                                error={Boolean(formik.errors.manufacturer) && Boolean(formik.touched.manufacturer)}
                                helperText={Boolean(formik.touched.manufacturer) && formik.errors.manufacturer}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="modelNumber"
                                name="modelNumber"
                                type="text"
                                value={formik.values.modelNumber}
                                label="Model Number"
                                onChange={handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.modelNumber) && Boolean(formik.touched.modelNumber)}
                                helperText={Boolean(formik.touched.modelNumber) && formik.errors.modelNumber}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="serialNumber"
                                name="serialNumber"
                                type="text"
                                value={formik.values.serialNumber}
                                label="Serial Number"
                                onChange={handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.serialNumber) && Boolean(formik.touched.serialNumber)}
                                helperText={Boolean(formik.touched.serialNumber) && formik.errors.serialNumber}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </div>
    );
};

EquipmentStep.defaultProps = {
    activeStep:            2,
    companyInfo:           {},
    equipmentTypes:        [],
    manufacturers:         [],
    targetEquipmentConfig: {},
    validateForm:          false
};

EquipmentStep.propTypes = {
    activeStep:            number,
    companyInfo:           object,
    equipmentTypes:        array,
    manufacturers:         array,
    targetEquipmentConfig: object,
    validateForm:          bool
};

const mapStateToProps = state => {
    return {
        activeStep:            state.wizard.activeStep,
        companyInfo:           state.companyInfo,
        equipmentTypes:        state.equipmentTypes.data,
        manufacturers:         state.architect.manufacturers,
        targetEquipmentConfig: state.wizard.targetEquipmentConfig,
        validateForm:          state.wizard.validateForm
    };
};

const mapDispatchToProps = {
    ...CoreActions,
    ...WizardActions
};

export default connect(mapStateToProps, mapDispatchToProps)(EquipmentStep);
